import Head from "next/head";
import { Box, Link, Stack, Typography } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import { constants } from "../../constants";

import { routes } from "@/global/routes";
import { throttleTime } from "@/global/utils/utils";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import type { TeamConfig } from "@/global/hooks/useTeamConfig";
import { useGenericModal } from "@/shared/common/GenericModal";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import { TransferTicketButton } from "@/shared/modules/ticketTransfer/TransferTicketButton";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Connect to Account Manager",
    description:
      "Connect your Las Vegas Ballpark Account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Games",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your Tickets will be available in your{" "}
        <Link
          href={"https://am.ticketmaster.com/aviatorslv/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Las Vegas Aviators
        </Link>{" "}
        account. Reach out to Live Fan Support for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How does the Aviators Groups work?",
    description:
      "Aviators Group Packages allow fans to select a minimum of 10 seats to any game. Simply log into your Las Vegas Ballpark Account manager and select games.",
  },
  {
    title: "Can I choose games throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are event tickets delivered?",
    description:
      "Get your tickets - Ticket will be available in the new Las Vegas Ballpark app or in your Account Manager.",
  },
];

export function CheckoutSuccessDescription() {
  const teamConfig = useTeamConfig();
  const { md: mdUp } = useCssMediaQuery("up");
  const router = useRouterExtended();
  const viewTicketsModal = useGenericModal();

  const onClickViewTicket = () => {
    viewTicketsModal.set({
      isOpen: true,
      title: <Box height="32px"></Box>,
      hideTitleIcon: true,
      description: (
        <Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            By clicking View Tickets, you will be redirected to the Las Vegas
            Aviators Account Manager to access your barcodes. Please note that
            any ticket transfers or forwarding completed there will not be
            reflected in this portal. For a seamless experience, please manage
            all transfers and forwarding through the Transfer Tickets portal.
          </Typography>
        </Box>
      ),

      closeIconProps: {
        onClick: () => {
          viewTicketsModal.set({ isOpen: false });
          throttleTime(400, () => {
            viewTicketsModal.reset();
          });
        },
      },

      closeButtonText: "View Tickets",
      closeButtonProps: {
        variant: "outlined",
      },
      onClickClose: () => {
        window.location.href = teamConfig.accountManagerUrl;
      },

      confirmButtonText: "Transfer Tickets",
      onClickConfirm: async () => {
        await router.push({
          pathname: routes.ticket_transfer,
          query: {
            ...router.query,
            eventCode: router?.query?.eventCode as string,
            orderNumber: Number(router?.query?.orderNumber),
          },
        });
      },
    });
  };

  return (
    <Box>
      <Typography fontSize={"1.1rem"} color={"text.primary"} lineHeight={1.4}>
        Your order was successful! If you&apos;d like to transfer tickets to
        members of your group. Click Transfer Tickets to get started.
      </Typography>
      <Stack
        direction={mdUp ? "row" : "column"}
        justifyContent={"center"}
        alignItems="center"
        spacing={2}
        mt={2.5}
      >
        <TransferTicketButton
          size="large"
          eventCode={router?.query?.eventCode as string}
          orderNumber={Number(router?.query?.orderNumber)}
          sx={{
            width: mdUp ? "inherit" : "100%",
          }}
        />

        <ModalActionButton
          variant="outlined"
          onClick={onClickViewTicket}
          size="large"
          sx={{
            width: mdUp ? "170px" : "100%",
          }}
        >
          View Tickets
        </ModalActionButton>
      </Stack>
    </Box>
  );
}

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '3360222884215614');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=3360222884215614&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};
